import type { DieSides, MasterDie } from '../core/types';
import { MATERIALS, isMaterialId, type MaterialId } from '../data/materials';
import { dieMaterialOf, isEnriched } from './abyssApi';
import { sidesRank, sortLevelOf } from './diceTabs';
import { readSessionPref, writeSessionPref } from './sessionPref';

/**
 * Tri et filtres des dés (v0.55.8, A119) — la **logique pure** du panneau
 * « Tri et filtres » : un seul état de vue pour tout le jeu (Inventaire,
 * Recycleur à l'unité, cibles de la Forge, fenêtre « Équiper l'emplacement N »),
 * mémorisé le temps de la session. Aucun DOM ici ; le panneau est
 * `screens/accalmie/diceViewPanel.ts`.
 */

export type DiceSortKey = 'level' | 'enriched' | 'power' | 'sides';
export type EnrichedFilter = 'all' | 'yes' | 'no';

export interface DiceView {
  /** `level` par défaut (A103 : niveau, faces, score). */
  sort: DiceSortKey;
  enriched: EnrichedFilter;
  material: MaterialId | 'all';
  sides: DieSides | 'all';
}

export const DEFAULT_DICE_VIEW: DiceView = Object.freeze({
  sort: 'level',
  enriched: 'all',
  material: 'all',
  sides: 'all',
}) as DiceView;

export const DICE_SORT_KEYS: readonly DiceSortKey[] = ['level', 'enriched', 'power', 'sides'];

/** Libellé de chaque clé de tri (liste déroulante). */
export const DICE_SORT_LABEL: Record<DiceSortKey, string> = {
  level: 'Niveau',
  enriched: 'Enrichi d’abord',
  power: 'Puissance',
  sides: 'Faces',
};

/** Libellé court d'une clé de tri (résumé : « Tri : Enrichi »). */
const SORT_SHORT: Record<DiceSortKey, string> = {
  level: 'Niveau',
  enriched: 'Enrichi',
  power: 'Puissance',
  sides: 'Faces',
};

export const ENRICHED_LABEL: Record<EnrichedFilter, string> = {
  all: 'Tous',
  yes: 'Enrichis seulement',
  no: 'Non enrichis',
};

const KNOWN_SIDES: readonly number[] = [4, 6, 12, 20, 100];

const VIEW_KEY = 'diceordie.dice.view';
const VIEW_OPEN_KEY = 'diceordie.dice.viewOpen';

// ───────────────────────────────────────────────────────── Mémoire de session

/** Lecture tolérante : une valeur illisible (ou un champ inconnu) retombe sur le défaut. */
export function readDiceView(): DiceView {
  const raw = readSessionPref(VIEW_KEY);
  if (!raw) return { ...DEFAULT_DICE_VIEW };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ...DEFAULT_DICE_VIEW };
  }
  if (typeof parsed !== 'object' || parsed === null) return { ...DEFAULT_DICE_VIEW };
  const p = parsed as Record<string, unknown>;
  return {
    sort: DICE_SORT_KEYS.includes(p.sort as DiceSortKey) ? (p.sort as DiceSortKey) : DEFAULT_DICE_VIEW.sort,
    enriched: p.enriched === 'yes' || p.enriched === 'no' ? p.enriched : 'all',
    material: isMaterialId(p.material) ? p.material : 'all',
    sides: typeof p.sides === 'number' && KNOWN_SIDES.includes(p.sides) ? (p.sides as DieSides) : 'all',
  };
}

export function writeDiceView(v: DiceView): void {
  writeSessionPref(VIEW_KEY, JSON.stringify(v));
}

/** Le panneau est replié par défaut ; son ouverture est mémorisée pour la session. */
export function readDiceViewOpen(): boolean {
  return readSessionPref(VIEW_OPEN_KEY) === '1';
}

export function writeDiceViewOpen(open: boolean): void {
  writeSessionPref(VIEW_OPEN_KEY, open ? '1' : '0');
}

// ───────────────────────────────────────────────────────────────── Lectures

export function isDefaultView(v: DiceView): boolean {
  return v.sort === 'level' && v.enriched === 'all' && v.material === 'all' && v.sides === 'all';
}

/** Un filtre (et pas seulement le tri) est-il actif ? */
export function hasActiveFilter(v: DiceView): boolean {
  return v.enriched !== 'all' || v.material !== 'all' || v.sides !== 'all';
}

/** Nom d'un Matériau (`MATERIALS`), l'identifiant à défaut. */
export function materialName(id: MaterialId): string {
  return MATERIALS.find((m) => m.id === id)?.name ?? id;
}

/** « Tri : Enrichi · Enrichis seulement · Boisé · d20 », ou « Tri : Niveau » pour la vue par défaut. */
export function viewSummary(v: DiceView): string {
  const parts = [`Tri : ${SORT_SHORT[v.sort]}`];
  if (v.enriched !== 'all') parts.push(ENRICHED_LABEL[v.enriched]);
  if (v.material !== 'all') parts.push(materialName(v.material));
  if (v.sides !== 'all') parts.push(`d${v.sides}`);
  return parts.join(' · ');
}

/** Matériaux portés par au moins un dé, dans l'ordre de `MATERIALS`. */
export function presentMaterials(dice: readonly MasterDie[]): MaterialId[] {
  const seen = new Set(dice.map((d) => dieMaterialOf(d)));
  return MATERIALS.map((m) => m.id).filter((id) => seen.has(id));
}

/** Faces présentes, du d100 au d4 (même ordre que les volets). */
export function presentSides(dice: readonly MasterDie[]): DieSides[] {
  return [...new Set(dice.map((d) => d.sides))].sort((a, b) => sidesRank(a) - sidesRank(b));
}

// ───────────────────────────────────────────────────────────── Filtre et tri

/** Filtres en ET ; la vue par défaut ne retire rien. */
export function filterDice(dice: readonly MasterDie[], v: DiceView): MasterDie[] {
  return dice.filter((d) => {
    if (v.enriched === 'yes' && !isEnriched(d)) return false;
    if (v.enriched === 'no' && isEnriched(d)) return false;
    if (v.material !== 'all' && dieMaterialOf(d) !== v.material) return false;
    if (v.sides !== 'all' && d.sides !== v.sides) return false;
    return true;
  });
}

/**
 * Tri par la clé de la vue ; à égalité, l'ordre d'arrivée départage.
 *  - `level` : niveau ↓ (un dé de campagne vaut 0), rang de faces, score ↓ (= `sortWithinPanel`) ;
 *  - `enriched` : Enrichis d'abord, puis comme `level` ;
 *  - `power` : score ↓, puis niveau ↓, rang de faces ;
 *  - `sides` : rang de faces (d100, d20, d12, d6, d4), puis niveau ↓, score ↓.
 */
export function sortDice(dice: readonly MasterDie[], v: DiceView, score: (d: MasterDie) => number): MasterDie[] {
  const rows = dice.map((die, index) => ({
    die,
    index,
    level: sortLevelOf(die),
    rank: sidesRank(die.sides),
    score: score(die),
    rich: isEnriched(die) ? 1 : 0,
  }));
  type Row = (typeof rows)[number];
  const byKey: Record<DiceSortKey, (a: Row, b: Row) => number> = {
    level: (a, b) => b.level - a.level || a.rank - b.rank || b.score - a.score,
    enriched: (a, b) => b.rich - a.rich || b.level - a.level || a.rank - b.rank || b.score - a.score,
    power: (a, b) => b.score - a.score || b.level - a.level || a.rank - b.rank,
    sides: (a, b) => a.rank - b.rank || b.level - a.level || b.score - a.score,
  };
  const cmp = byKey[v.sort];
  return rows.sort((a, b) => cmp(a, b) || a.index - b.index).map((r) => r.die);
}
